import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2, Save, X, DollarSign, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TariffManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tariffs, setTariffs] = useState([]);
  const [filterMagazine, setFilterMagazine] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [role, setRole] = useState('');
  
  const [formData, setFormData] = useState({
    magazine: 'Aabkari Times',
    plan_type: 'Registered Post',
    duration: '1 Year',
    amount: '',
    description: '',
    is_active: true
  });

  const planTypes = ['Registered Post', 'E-Magazine', 'Member-Advt.', 'Complimentary', 'Life'];
  const durations = ['1 Year', '2 Years', '3 Years', '5 Years', 'Lifetime'];

  useEffect(() => {
    fetchTariffs();
    const storedRole = localStorage.getItem('role');
    setRole(storedRole || '');
  }, [filterMagazine]);

  const fetchTariffs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = filterMagazine ? `?magazine=${encodeURIComponent(filterMagazine)}` : '';
      const response = await axios.get(`${API}/tariffs${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTariffs(response.data);
    } catch (error) {
      console.error('Error fetching tariffs:', error);
      toast.error('Failed to fetch tariffs');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will add default tariff plans. Existing tariffs will not be affected. Continue?')) {
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/tariffs/seed-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchTariffs();
    } catch (error) {
      console.error('Error seeding defaults:', error);
      toast.error(error.response?.data?.detail || 'Failed to seed defaults');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      magazine: 'Aabkari Times',
      plan_type: 'Registered Post',
      duration: '1 Year',
      amount: '',
      description: '',
      is_active: true
    });
    setShowAddModal(true);
  };

  const handleEdit = (tariff) => {
    setFormData({
      magazine: tariff.magazine,
      plan_type: tariff.plan_type,
      duration: tariff.duration,
      amount: tariff.amount.toString(),
      description: tariff.description || '',
      is_active: tariff.is_active !== false
    });
    setEditingTariff(tariff);
    setShowAddModal(true);
  };

  const handleSave = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editingTariff) {
        await axios.put(`${API}/tariffs/${editingTariff.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tariff updated successfully');
      } else {
        await axios.post(`${API}/tariffs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tariff created successfully');
      }

      setShowAddModal(false);
      setEditingTariff(null);
      fetchTariffs();
    } catch (error) {
      console.error('Error saving tariff:', error);
      toast.error(error.response?.data?.detail || 'Failed to save tariff');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tariff) => {
    if (!window.confirm(`Delete tariff "${tariff.plan_type} - ${tariff.duration}" for ${tariff.magazine}?`)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tariffs/${tariff.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tariff deleted successfully');
      fetchTariffs();
    } catch (error) {
      console.error('Error deleting tariff:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required to delete tariffs');
      } else {
        toast.error('Failed to delete tariff');
      }
    } finally {
      setLoading(false);
    }
  };

  const groupedTariffs = tariffs.reduce((acc, tariff) => {
    const key = tariff.magazine;
    if (!acc[key]) acc[key] = [];
    acc[key].push(tariff);
    return acc;
  }, {});

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
                <ArrowLeft className="mr-2" size={18} />
                Back
              </Button>
              <h1 className="text-4xl font-bold text-purple-700" data-testid="page-title">
                <DollarSign className="inline mr-2" size={36} />
                Tariff Management
              </h1>
            </div>
            <div className="flex space-x-3">
              {role === 'admin' && (
                <Button onClick={handleSeedDefaults} variant="outline" disabled={loading} data-testid="seed-defaults-btn">
                  <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
                  Seed Defaults
                </Button>
              )}
              <Button onClick={handleAdd} className="bg-purple-600 hover:bg-purple-700" data-testid="add-tariff-btn">
                <Plus className="mr-2" size={18} />
                Add Tariff
              </Button>
            </div>
          </div>

          {/* Filter */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center space-x-4">
              <Label className="font-semibold">Filter by Magazine:</Label>
              <Select value={filterMagazine || "all"} onValueChange={(v) => setFilterMagazine(v === "all" ? "" : v)}>
                <SelectTrigger className="w-64" data-testid="filter-magazine">
                  <SelectValue placeholder="All Magazines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Magazines</SelectItem>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
              {filterMagazine && (
                <Button variant="ghost" size="sm" onClick={() => setFilterMagazine('')}>
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Tariff Tables */}
          {loading && tariffs.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading tariffs...</p>
            </div>
          ) : tariffs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <DollarSign className="mx-auto mb-4 text-gray-400" size={64} />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Tariffs Found</h3>
              <p className="text-gray-500 mb-4">
                {filterMagazine ? `No tariffs for ${filterMagazine}` : 'Get started by adding tariff plans or seeding defaults.'}
              </p>
              {role === 'admin' && (
                <Button onClick={handleSeedDefaults} className="bg-purple-600 hover:bg-purple-700">
                  <RefreshCw className="mr-2" size={18} />
                  Seed Default Tariffs
                </Button>
              )}
            </div>
          ) : (
            Object.entries(groupedTariffs).map(([magazine, magazineTariffs]) => (
              <div key={magazine} className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4" style={{ color: (magazine === 'Aabkari Times' || magazine === 'Aabkari Times ENG') ? '#2563eb' : '#059669' }}>
                  {magazine}
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse" data-testid={`tariff-table-${magazine.replace(' ', '-').toLowerCase()}`}>
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border px-4 py-3 text-left">Plan Type</th>
                        <th className="border px-4 py-3 text-left">Duration</th>
                        <th className="border px-4 py-3 text-right">Amount (₹)</th>
                        <th className="border px-4 py-3 text-left">Description</th>
                        <th className="border px-4 py-3 text-center">Status</th>
                        <th className="border px-4 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {magazineTariffs.sort((a, b) => {
                        if (a.plan_type !== b.plan_type) return a.plan_type.localeCompare(b.plan_type);
                        const durationOrder = {'1 Year': 1, '2 Years': 2, '3 Years': 3, '5 Years': 5, 'Lifetime': 99};
                        return (durationOrder[a.duration] || 0) - (durationOrder[b.duration] || 0);
                      }).map((tariff) => (
                        <tr key={tariff.id} className="hover:bg-gray-50" data-testid={`tariff-row-${tariff.id}`}>
                          <td className="border px-4 py-3 font-medium">{tariff.plan_type}</td>
                          <td className="border px-4 py-3">{tariff.duration}</td>
                          <td className="border px-4 py-3 text-right font-semibold text-green-700">
                            ₹{tariff.amount.toLocaleString('en-IN')}
                          </td>
                          <td className="border px-4 py-3 text-gray-600">{tariff.description || '-'}</td>
                          <td className="border px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${tariff.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {tariff.is_active !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="border px-4 py-3 text-center">
                            <div className="flex justify-center space-x-2">
                              <Button size="sm" variant="outline" onClick={() => handleEdit(tariff)} data-testid={`edit-${tariff.id}`}>
                                <Edit size={14} />
                              </Button>
                              {role === 'admin' && (
                                <Button size="sm" variant="destructive" onClick={() => handleDelete(tariff)} data-testid={`delete-${tariff.id}`}>
                                  <Trash2 size={14} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}

          {/* Add/Edit Modal */}
          {showAddModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-purple-700">
                    {editingTariff ? 'Edit Tariff' : 'Add New Tariff'}
                  </h2>
                  <Button variant="ghost" size="sm" onClick={() => { setShowAddModal(false); setEditingTariff(null); }}>
                    <X size={20} />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Magazine *</Label>
                    <Select value={formData.magazine} onValueChange={(v) => setFormData({ ...formData, magazine: v })}>
                      <SelectTrigger data-testid="modal-magazine">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                        <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Plan Type *</Label>
                    <Select value={formData.plan_type} onValueChange={(v) => setFormData({ ...formData, plan_type: v })}>
                      <SelectTrigger data-testid="modal-plan-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {planTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Duration *</Label>
                    <Select value={formData.duration} onValueChange={(v) => setFormData({ ...formData, duration: v })}>
                      <SelectTrigger data-testid="modal-duration">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {durations.map((dur) => (
                          <SelectItem key={dur} value={dur}>{dur}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Amount (₹) *</Label>
                    <Input
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="Enter amount"
                      data-testid="modal-amount"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional description"
                      data-testid="modal-description"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="is_active">Active</Label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <Button variant="outline" onClick={() => { setShowAddModal(false); setEditingTariff(null); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="modal-save">
                    <Save className="mr-2" size={16} />
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TariffManagement;
